import { Link } from "react-router-dom";
import { FileText, Scale, HeartPulse, Users, AlertTriangle, ArrowLeft } from "lucide-react";
import { LEGAL_EFFECTIVE_DATE, LEGAL_VERSION, SUPPORT_EMAIL } from "../lib/legal";
import { useSettings } from "../context/SettingsContext";

const TEAL = "#0D9488";

function Section({ id, icon: Icon, title, children }) {
  return (
    <section id={id} className="mb-10 scroll-mt-24">
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="w-5 h-5" style={{ color: TEAL }} />}
        <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
      </div>
      <div className="space-y-3 text-[15px] leading-relaxed text-slate-700">
        {children}
      </div>
    </section>
  );
}

/**
 * /terms — Terms of Service. Written in plain English, structured so the
 * medical-disclaimer + user-data-rights + coaching sections are legally
 * explicit but human-readable. Intentionally paired with /privacy so the two
 * documents cover the full scope the product owner asked for.
 */
export default function Terms() {
  const { settings } = useSettings();
  const site = settings?.site_name || "The Amino Exchange";
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-sm font-semibold text-slate-900 hover:text-teal-700 flex items-center gap-2" data-testid="terms-back-home">
            <ArrowLeft className="w-4 h-4" /> {site}
          </Link>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Legal · v{LEGAL_VERSION}</div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10" data-testid="terms-page">
        <div className="mb-8">
          <div className="text-[11px] font-semibold uppercase tracking-widest mb-2" style={{ color: TEAL }}>Legal</div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Terms of Service</h1>
          <p className="text-slate-500 text-sm">Effective {LEGAL_EFFECTIVE_DATE} · Last updated {LEGAL_EFFECTIVE_DATE}</p>
        </div>

        <div className="mb-8 rounded-lg border border-teal-200 bg-teal-50/50 p-4 text-sm text-teal-900" data-testid="terms-intro">
          <b>Plain-English summary.</b> {site} is a tracking journal — you keep your logs, we keep
          them safe. This is <em>not</em> a medical service. You must be 18+. Don't be a jerk in the
          community. If you break these terms, we can suspend your account. And of course you can
          delete your account whenever you want.
        </div>

        <Section id="acceptance" icon={FileText} title="1. Acceptance of these Terms">
          <p>
            By creating an account, checking "I agree to the Privacy Policy and Terms of Service"
            during signup, or otherwise using <b>{site}</b> (the "Service"), you enter into a
            binding agreement with the operator of the Service ("we", "us"). If you don't agree,
            please don't use the Service.
          </p>
          <p>
            These Terms incorporate our <Link to="/privacy" className="text-teal-700 hover:underline">Privacy Policy</Link> by reference.
          </p>
        </Section>

        <Section id="eligibility" icon={Users} title="2. Eligibility">
          <p>You must be at least <b>21 years old</b> and legally able to enter contracts in your
            jurisdiction to use the Service. Because the Service tracks peptides, hormones, and other
            regulated compounds, we require adult-age attestation at signup. By registering, you
            represent that you meet these requirements.</p>
        </Section>

        <Section id="account" icon={Scale} title="3. Your account">
          <p>You are responsible for keeping your login credentials confidential and for every
            activity that occurs under your account. Notify us at
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-teal-700 hover:underline"> {SUPPORT_EMAIL}</a> if
            you suspect unauthorized access.</p>
          <p>We may refuse, suspend, or terminate accounts that we reasonably believe are engaged
            in fraud, abuse, harassment, spam, or otherwise violate these Terms. We'll try to give
            you notice and a chance to appeal wherever feasible.</p>
        </Section>

        <Section id="use" icon={FileText} title="4. Acceptable use">
          <p>You agree not to:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Use the Service to break any law or infringe anyone's rights.</li>
            <li>Post medical advice, dosage recommendations, or "prescriptions" to other users. The
              Service is a tracking tool, not a clinical platform.</li>
            <li>Harass, threaten, dox, or impersonate other users, coaches, or staff.</li>
            <li>Reverse-engineer, scrape, resell, or abuse the API or web app.</li>
            <li>Circumvent security, rate limits, or paid-feature gates.</li>
            <li>Upload malware, illegal content, or copyrighted material you do not own.</li>
          </ul>
        </Section>

        <Section id="community-standards" icon={Users} title="5. Community standards & moderation">
          <div className="rounded-md border-l-4 border-red-500 bg-red-50 px-4 py-3 mb-3">
            <div className="font-mono-num text-[11px] uppercase tracking-widest text-red-700 font-black mb-1">// Take these rules seriously</div>
            <p className="text-red-900 text-sm">
              Violating any of these standards may cause your content to be <b className="uppercase">Removed</b> and your account to be <b className="uppercase">Banned</b>. Repeat offenders are automatically muted and, in severe cases, permanently banned.
            </p>
          </div>
          <p>By using the community, discussion boards, direct messages, group chats, and any other
            member-to-member feature, you agree to:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><b>Treat each other with respect.</b> No harassment, targeted attacks, slurs, hateful
              conduct, or personal insults — even in a heated discussion.</li>
            <li><b>No doxxing.</b> Never post another member's home address, workplace, phone
              number, medical records, or any other personal information without their explicit
              consent.</li>
            <li><b>No illegal content.</b> Do not post, share, or arrange the sale of anything
              that is unlawful in your jurisdiction or ours.</li>
            <li><b>No spam.</b> No advertising, referral link farms, off-topic promotion, or
              coordinated inauthentic behavior.</li>
            <li><b>Report, don't retaliate.</b> If someone crosses the line, use the in-app Report
              button rather than fighting back — staff will handle it.</li>
          </ul>
          <p className="mt-3">
            <b>Our right to review and remove.</b> By using {site} you acknowledge that our staff
            (admins and moderators) have the right to <b className="uppercase bg-red-100 px-1 py-0.5 border border-red-300">Remove</b> any content
            that is reported for violating these standards — including harassment, spam, doxxing,
            illegal content, and misinformation. Repeated violations may result in your account
            being <b className="uppercase bg-red-100 px-1 py-0.5 border border-red-300">Banned</b> from the platform without refund.
          </p>
          <p>
            <b>Auto-mute for repeat offenders.</b> To keep the community safe, an account that
            accumulates three Removed reports in a rolling seven-day window is automatically
            paused from posting and messaging for 24 hours. Admins are notified so they can
            decide whether a permanent ban is warranted.
          </p>
          <p>
            <b>Data usage for moderation.</b> Logs of reported content — including a short snippet
            of the reported item, who authored it, who reported it, and any staff action — are
            stored and reviewed <em>strictly for the purpose of community safety</em>. They are
            never used for marketing, sold to third parties, or exposed to any user outside the
            reporter and the staff acting on the report. See the
            <a className="text-teal-700 hover:underline font-semibold" href="/privacy#moderation"> Privacy Policy §7</a> for
            the full data-handling details.
          </p>
        </Section>

        <Section id="medical" icon={HeartPulse} title="6. Medical disclaimer (please read carefully)">
          <p>
            <b>{site} is a personal wellness tracker. It is <em>not</em> a medical device, a
            pharmacy, a clinic, or a substitute for professional healthcare advice.</b>
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Everything we display — peptide entries in the library, reconstitution
              calculators, side-effect lists, community posts, AI meal parses, coach guidance — is
              informational. It is not a diagnosis, a prescription, or a recommendation of
              treatment.</li>
            <li>Peptides, hormones, injectable compounds, and supplements can cause serious harm,
              interact with medications, and are subject to laws that vary by jurisdiction. It is
              your responsibility to confirm what is legal, safe, and appropriate for you with a
              licensed healthcare professional before you use them.</li>
            <li>AI-generated macros, dose reminders, and countdowns are convenience features and
              may be inaccurate. Do not rely on them for clinical, medical, or life-safety
              decisions.</li>
            <li>Coaches on the Service are not, unless expressly identified as such, licensed
              physicians. Their guidance is peer-to-peer support, not medical advice.</li>
            <li>You use the Service at your own risk. To the maximum extent permitted by law, we
              disclaim all liability for injury, illness, or damages arising out of decisions you
              make based on information logged, displayed, or generated by the Service.</li>
          </ul>
          <p><b>If you are experiencing a medical emergency, call your local emergency
            services immediately.</b></p>
        </Section>

        <Section id="coaching" icon={Users} title="7. Coaching feature">
          <p>The Coaching feature lets you optionally connect with a peer coach. When you connect
            with a coach:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>You grant that coach access to your logged shots, cycles, nutrition, and weight
              data for the duration of the coaching relationship.</li>
            <li>Coaches agree to keep your data confidential and use it solely to guide you.</li>
            <li>You can end the relationship at any time from your Profile; the coach will lose
              access to any new data going forward.</li>
            <li>Coaches are independent users of the Service. Any advice they offer is their own —
              we do not endorse it, we do not verify credentials unless we explicitly certify
              them, and we are not a party to your relationship with your coach.</li>
          </ul>
        </Section>

        <Section id="your-content" icon={Scale} title="8. Your content, your data">
          <p>You retain ownership of everything you log — your shots, meals, weights, posts,
            messages, photos, and notes. You grant us a limited, worldwide, royalty-free license
            to store, process, and display that content <em>solely</em> to operate the Service for
            you.</p>
          <p>You are free to export or delete your data at any time. See our
            <Link to="/privacy#your-rights" className="text-teal-700 hover:underline"> Privacy Policy</Link> for
            the specifics.</p>
        </Section>

        <Section id="payments" icon={Scale} title="9. Paid features & subscriptions">
          <p>Some features (e.g. Pro) are available for a fee. When you subscribe, you authorize
            our payment providers (Stripe, PayPal, Venmo) to charge your chosen method on the
            billing cadence shown at checkout. You can cancel any time from your Profile;
            cancellation takes effect at the end of the current billing period.</p>
          <p>Refund policy: we generally do not offer prorated refunds for partial billing
            periods, but we consider requests case-by-case at
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-teal-700 hover:underline"> {SUPPORT_EMAIL}</a>. If we
            change subscription pricing, we'll notify you at least 30 days before your next
            renewal.</p>
        </Section>

        <Section id="warranties" icon={AlertTriangle} title="10. Warranties & liability">
          <p>The Service is provided <b>"as is"</b> and <b>"as available"</b>. We disclaim all
            warranties to the fullest extent permitted by law, express or implied, including
            merchantability, fitness for a particular purpose, and non-infringement.</p>
          <p>To the maximum extent permitted by law, our aggregate liability arising out of or
            related to the Service will not exceed the greater of (a) the total amount you paid us
            for the Service in the twelve months preceding the claim or (b) US $100.</p>
          <p>Nothing in these Terms is intended to exclude liability for our gross negligence,
            fraud, or any other liability that cannot be excluded under applicable law.</p>
        </Section>

        <Section id="indemnity" icon={AlertTriangle} title="11. Indemnity">
          <p>You agree to defend, indemnify, and hold us harmless from any third-party claim
            arising out of (a) your violation of these Terms, (b) your content, or (c) your
            misuse of the Service.</p>
        </Section>

        <Section id="termination" icon={Scale} title="12. Termination">
          <p>You may stop using the Service at any time and delete your account by emailing
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-teal-700 hover:underline"> {SUPPORT_EMAIL}</a>. We
            may suspend or <b className="uppercase bg-red-100 px-1 py-0.5 border border-red-300">Ban</b> your access if you
            materially breach these Terms — including repeated violations of the community
            standards in §5.</p>
          <p>Sections that by their nature should survive termination (e.g. medical disclaimer,
            warranties, indemnity, liability limits) will do so.</p>
        </Section>

        <Section id="law" icon={FileText} title="13. Governing law & disputes">
          <p>These Terms are governed by the laws of the jurisdiction where the operator of the
            Service is established, without regard to conflict-of-laws principles. Both parties
            will attempt to resolve any dispute informally first by contacting
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-teal-700 hover:underline"> {SUPPORT_EMAIL}</a>. If
            we can't, disputes will be resolved in the courts of that jurisdiction, unless
            applicable consumer-protection law grants you a different forum.</p>
        </Section>

        <Section id="changes" icon={FileText} title="14. Changes to these Terms">
          <p>We may update these Terms occasionally. When we do, we'll change the "Last updated"
            date above and — for material changes — email you or ask you to re-accept the
            updated Terms next time you log in.</p>
        </Section>

        <Section id="contact" icon={FileText} title="15. Contact us">
          <p>Questions? Reach us at
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-teal-700 hover:underline font-semibold"> {SUPPORT_EMAIL}</a>.</p>
        </Section>

        <div className="mt-12 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-500 border-t border-slate-200 pt-6">
          <Link to="/privacy" className="text-teal-700 hover:underline font-semibold" data-testid="terms-to-privacy">
            Read the Privacy Policy →
          </Link>
          <Link to="/" className="hover:underline" data-testid="terms-home">Back to {site}</Link>
        </div>
      </main>
    </div>
  );
}
